import { mostrarToast } from "../utils/toast";
import { useAuth } from "../context/AuthContext";
import "react-toastify/dist/ReactToastify.css";
import React, { useEffect, useState } from "react";
import { useEmpresa } from "../context/EmpresaContext";
import {
  CreditCard,
  Banknote,
  Wallet,
  Coins,
  Pencil,
  Trash2,
} from "lucide-react";
import { api } from "../api";

// Ícones por condição de pagamento
const iconesPorCondicao = {
  DEBITO: <CreditCard className="w-6 h-6 text-blue-400" />,
  CREDITO: <CreditCard className="w-6 h-6 text-blue-400" />,
  BRASILCARD: <CreditCard className="w-6 h-6 text-orange-500" />,
  PIX: <Banknote className="w-6 h-6 text-blue-400" />,
  DINHEIRO: <Coins className="w-6 h-6 text-blue-400" />,
  "SAÍDA DE CAIXA": <Wallet className="w-6 h-6 text-red-600" />,
  "CREDITO PARCELADO SS": <CreditCard className="w-6 h-6 text-fuchsia-500" />,

  // NOVAS (MP)
  "DEBITO MP": <CreditCard className="w-6 h-6 text-blue-400" />,
  "CREDITO MP": <CreditCard className="w-6 h-6 text-blue-400" />,
  "CREDITO PARCELADO MP": <CreditCard className="w-6 h-6 text-blue-400" />,
  BOLETO: <Banknote className="w-6 h-6 text-green-600" />,

  // NOVAS (ADICIONADAS AGORA)
  "LINK PAG": <CreditCard className="w-6 h-6 text-indigo-500" />,
  "PIX CNPJ": <Banknote className="w-6 h-6 text-emerald-600" />,
};

function formatarMoeda(valor) {
  const numero = parseFloat(valor) / 100;
  return numero.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

const getHojeLocalBR = () => {
  const agora = new Date();
  const ano = agora.getFullYear();
  const mes = String(agora.getMonth() + 1).padStart(2, "0");
  const dia = String(agora.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

export default function FluxoCaixa() {
  const { usuario, carregando } = useAuth();

  if (carregando) return <div>Carregando usuário...</div>;
  if (!usuario || !usuario.empresa_id) return <div>Usuário inválido</div>;

  // Admin = perfil/cargo/role contendo "admin" ou flag booleana is_admin
  const isAdmin =
    String(usuario?.perfil || usuario?.cargo || usuario?.role || "")
      .toLowerCase()
      .includes("admin") || usuario?.is_admin === true;

  const { empresaAtiva } = useEmpresa();
  const [pagamentos, setPagamentos] = useState([]);
  const [dentistas, setDentistas] = useState([]);
  const [form, setForm] = useState({
    nome_paciente: "",
    valor: "",
    condicao_pagamento: "",
    data_pagamento: getHojeLocalBR(),
    dentista_id: "",
  });
  const [editandoId, setEditandoId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dataFiltro, setDataFiltro] = useState(getHojeLocalBR());

  // Salva a empresa ativa no localStorage (sem tocar em api.defaults)
  useEffect(() => {
    if (empresaAtiva?.id) {
      localStorage.setItem("empresa_id", String(empresaAtiva.id));
    }
  }, [empresaAtiva?.id]);

  // Buscar dentistas ao montar/alterar empresa
  useEffect(() => {
    const fetchDentistas = async () => {
      try {
        const empresaId = empresaAtiva?.id;
        if (!empresaId) {
          setDentistas([]);
          return;
        }
        const res = await api.get(`/dentistas/${empresaId}`);
        if (Array.isArray(res)) setDentistas(res);
        else if (Array.isArray(res.data)) setDentistas(res.data);
        else setDentistas([]);
      } catch {
        setDentistas([]);
      }
    };
    fetchDentistas();
  }, [empresaAtiva?.id]);

  // Buscar pagamentos conforme filtro de data
  const fetchPagamentos = async (empresa_id, data) => {
    if (!empresa_id || empresa_id === "undefined") {
      console.warn("❌ ERRO: empresa_id ausente ou inválido!", empresa_id);
      return;
    }
    setLoading(true);

    const token = localStorage.getItem("token");

    try {
      const response = await fetch(
        `https://api.captacrm.com.br/pagamentos?empresa_id=${empresa_id}&data=${data}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const textoResposta = await response.text();
      if (!response.ok) {
        throw new Error(
          `Erro ${response.status} ${response.statusText}: ${textoResposta}`,
        );
      }
      let dataResposta;
      try {
        dataResposta = JSON.parse(textoResposta);
      } catch {
        throw new Error("Resposta não está em JSON válido");
      }
      setPagamentos(
        Array.isArray(dataResposta) ? dataResposta : dataResposta.data || [],
      );
    } catch (err) {
      console.error("❌ Erro ao buscar pagamentos:", err);
      setPagamentos([]);
    } finally {
      setLoading(false);
    }
  };

  const recarregarPagamentos = () => {
    const empresa_id = empresaAtiva?.id;
    if (empresa_id) {
      fetchPagamentos(empresa_id, dataFiltro);
    } else {
      console.warn(
        "❌ [recarregarPagamentos] empresa_id inválido, não chamou fetch",
      );
    }
  };

  // Recarrega pagamentos sempre que empresa ou data mudar
  useEffect(() => {
    if (empresaAtiva?.id) recarregarPagamentos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresaAtiva?.id, dataFiltro]);

  // Manipulação dos campos do formulário
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "valor") {
      const somenteNumeros = value.replace(/[^\d]/g, "");
      const valorFormatado = formatarMoeda(somenteNumeros);
      setForm({ ...form, valor: valorFormatado });
    } else if (name === "nome_paciente") {
      setForm({ ...form, [name]: value.toUpperCase() });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleBlurValor = () => {
    let valor = form.valor.replace(/[^\d,]/g, "").replace(",", ".");
    let numero = parseFloat(valor);
    if (!isNaN(numero)) {
      setForm({ ...form, valor: numero.toFixed(2).replace(".", ",") });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (
      !form.nome_paciente ||
      !form.valor ||
      !form.condicao_pagamento ||
      !form.data_pagamento ||
      !form.dentista_id
    ) {
      mostrarToast("alerta", "⚠️ Preencha todos os campos obrigatórios.");
      return;
    }

    const hoje = getHojeLocalBR();
    const dataSelecionada = form.data_pagamento;

    // Admin OU permissão podem lançar fora do dia (nunca futuro)
    if (!(isAdmin || usuario.permissoes?.["fluxo_caixa.lancar_fora_do_dia"])) {
      if (dataSelecionada !== hoje) {
        mostrarToast(
          "erro",
          "⛔ Você só pode lançar pagamentos com a data de hoje.",
        );
        return;
      }
    } else {
      if (dataSelecionada > hoje) {
        mostrarToast(
          "erro",
          "⛔ Data inválida! Não é permitido lançar pagamentos com data futura.",
        );
        return;
      }
    }

    // Sempre usar a empresa ativa
    const empresaIdPayload = Number(empresaAtiva?.id);
    if (!empresaIdPayload) {
      mostrarToast(
        "erro",
        "❌ Empresa ativa inválida. Selecione a empresa novamente.",
      );
      return;
    }

    const valorNumerico = parseFloat(
      form.valor.replace(/[^\d,]/g, "").replace(",", "."),
    );
    if (isNaN(valorNumerico) || valorNumerico <= 0) {
      mostrarToast(
        "erro",
        "❌ Valor inválido. Informe um número maior que zero.",
      );
      return;
    }

    const payload = {
      ...form,
      valor:
        form.condicao_pagamento === "SAÍDA DE CAIXA"
          ? -Math.abs(valorNumerico)
          : valorNumerico,
      nome_paciente: form.nome_paciente.trim().toUpperCase(),
      empresa_id: empresaIdPayload, // <- empresa ativa
      dentista_id: Number(form.dentista_id),
    };

    const endpoint = editandoId ? `/pagamentos/${editandoId}` : "/pagamentos";
    const operacao = editandoId
      ? api.put(endpoint, payload)
      : api.post(endpoint, payload);

    operacao
      .then(() => {
        setForm({
          nome_paciente: "",
          valor: "",
          condicao_pagamento: "",
          data_pagamento: getHojeLocalBR(),
          dentista_id: "",
        });
        setDataFiltro(getHojeLocalBR());
        recarregarPagamentos();
        setEditandoId(null);

        if (form.condicao_pagamento === "SAÍDA DE CAIXA") {
          mostrarToast(
            "erro",
            "🔴 Pagamento (saída de caixa) lançado com sucesso.",
          );
        } else {
          mostrarToast("sucesso", "🟢 Recebimento lançado com sucesso.");
        }
      })
      .catch(() => mostrarToast("erro", "❌ Erro ao salvar o lançamento."));
  };

  const handleEditar = (pg) => {
    setForm({
      nome_paciente: pg.nome_paciente,
      valor: String(Math.round(Math.abs(pg.valor) * 100)),
      condicao_pagamento: pg.condicao_pagamento,
      data_pagamento: pg.data_pagamento,
      dentista_id: pg.dentista_id || "",
    });
    setEditandoId(pg.id);
  };

  const handleExcluir = (id) => {
    if (window.confirm("Tem certeza que deseja excluir este lançamento?")) {
      api
        .delete(`/pagamentos/${id}`)
        .then(() => {
          mostrarToast("alerta", "🗑️ Lançamento excluído com sucesso!");
          recarregarPagamentos();
        })
        .catch(() => mostrarToast("erro", "❌ Erro ao excluir pagamento."));
    }
  };

  // Totais
  const totaisPorCondicao = pagamentos.reduce((acc, curr) => {
    const cond = curr.condicao_pagamento;
    const valor = parseFloat(curr.valor);
    acc[cond] = (acc[cond] || 0) + (isNaN(valor) ? 0 : valor);
    return acc;
  }, {});
  const totalGeral = Object.values(totaisPorCondicao).reduce(
    (acc, val) => acc + val,
    0,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 to-blue-700 text-white p-2 sm:p-6">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl p-2 sm:p-6 relative">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-4 relative">
          <h1 className="text-2xl sm:text-4xl font-bold text-blue-800 tracking-tight text-center sm:text-left">
            💳 Fluxo de Caixa
          </h1>
          <div className="bg-blue-100 border border-blue-300 text-blue-900 rounded-xl px-2 py-1 sm:px-4 sm:py-2 shadow-sm w-full sm:w-auto flex items-center justify-center">
            <label className="font-semibold mr-2">Selecionar Data:</label>
            <input
              type="date"
              value={dataFiltro}
              onChange={(e) => {
                if (
                  isAdmin ||
                  usuario?.permissoes?.["fluxo_caixa.selecionar_data"]
                ) {
                  setDataFiltro(e.target.value);
                }
              }}
              disabled={
                !(
                  isAdmin ||
                  usuario?.permissoes?.["fluxo_caixa.selecionar_data"]
                )
              }
              className={`bg-transparent outline-none border border-blue-400 rounded-lg px-2 py-2 sm:px-3 shadow-sm w-full sm:w-auto ${
                !(
                  isAdmin ||
                  usuario?.permissoes?.["fluxo_caixa.selecionar_data"]
                )
                  ? "text-gray-400 cursor-not-allowed bg-gray-100"
                  : "text-blue-900"
              }`}
            />
          </div>
        </div>

        {/* FORMULÁRIO */}
        <form
          onSubmit={handleSubmit}
          className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-6 bg-white p-3 sm:p-6 rounded-xl shadow-md"
        >
          <div className="flex flex-col">
            <label className="text-blue-800 font-semibold mb-1">
              Nome / Motivo
            </label>
            <input
              type="text"
              name="nome_paciente"
              value={form.nome_paciente}
              onChange={handleChange}
              className="p-3 rounded-xl border border-blue-400 text-blue-900 bg-white shadow-sm"
              required
              autoComplete="off"
            />
          </div>

          <div className="flex flex-col">
            <label className="text-blue-800 font-semibold mb-1">
              Valor (R$)
            </label>
            <input
              type="text"
              name="valor"
              value={form.valor}
              onChange={handleChange}
              onBlur={handleBlurValor}
              className="p-3 rounded-xl border border-blue-400 text-blue-900 bg-white shadow-sm"
              required
              inputMode="numeric"
              autoComplete="off"
            />
          </div>

          {/* Dropdown dentistas */}
          <div className="flex flex-col">
            <label className="text-blue-800 font-semibold mb-1">
              Dentista Responsável
            </label>
            <select
              name="dentista_id"
              value={form.dentista_id || ""}
              onChange={handleChange}
              className="p-3 rounded-xl border border-blue-400 text-blue-900 bg-white shadow-sm"
              required
            >
              <option value="">Selecione</option>
              {dentistas.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.profissional}
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className="text-blue-800 font-semibold mb-1">Condição</label>
            <select
              name="condicao_pagamento"
              value={form.condicao_pagamento}
              onChange={handleChange}
              className="p-3 rounded-xl border border-blue-400 text-blue-900 bg-white shadow-sm"
              required
            >
              <option value="">Selecione</option>

              <option value="DINHEIRO">Dinheiro</option>
              <option value="DEBITO">Débito</option>
              <option value="CREDITO">Crédito</option>
              <option value="BRASILCARD">BrasilCard</option>
              <option value="CREDITO PARCELADO">Crédito Parcelado</option>
              <option value="CREDITO PARCELADO SS">Crédito Parcelado SS</option>

              <option value="PIX">PIX</option>
              <option value="PIX CNPJ">Pix CNPJ</option>
              <option value="LINK PAG">Link Pag</option>

              {/* NOVAS (MP) */}
              <option value="DEBITO MP">Débito MP</option>
              <option value="CREDITO MP">Crédito MP</option>
              <option value="CREDITO PARCELADO MP">Crédito Parcelado MP</option>
              <option value="BOLETO">Boleto</option>

              <option value="AMIL">Amil</option>
              <option value="PORTO SEGURO">Porto Seguro</option>
              <option value="HAPVIDA">Hapvida</option>
              <option value="SAÍDA DE CAIXA">Saída de Caixa</option>
            </select>
          </div>

          <div className="flex flex-col sm:col-span-2 md:col-span-1">
            <label className="text-blue-800 font-semibold mb-1">Data</label>
            <input
              type="date"
              name="data_pagamento"
              value={form.data_pagamento}
              onChange={handleChange}
              className="p-3 rounded-xl border border-blue-400 text-blue-900 bg-white shadow-sm"
              required
            />
          </div>

          <div className="md:col-span-4 sm:col-span-2 col-span-1">
            <button
              type="submit"
              disabled={loading}
              className={`mt-4 w-full ${
                editandoId
                  ? "bg-green-700 hover:bg-green-800"
                  : "bg-blue-700 hover:bg-blue-800"
              } text-white py-3 rounded-xl font-semibold transition ${
                loading ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {editandoId ? "Salvar Alterações" : "Lançar Pagamento"}
            </button>
          </div>
        </form>

        {/* TOTALIZADORES */}
        <div className="mt-8 pt-4 border-t-4 border-blue-300">
          <div className="flex items-center gap-2 mb-4">
            <Coins className="text-yellow-500 w-6 h-6" />
            <h2 className="text-xl font-bold text-blue-800">Totalizadores</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            {[
              "DINHEIRO",
              "DEBITO",
              "CREDITO",
              "BRASILCARD",
              "CREDITO PARCELADO",
              "CREDITO PARCELADO SS",
              "PIX",
              "PIX CNPJ",
              "LINK PAG",

              // NOVAS (MP)
              "DEBITO MP",
              "CREDITO MP",
              "CREDITO PARCELADO MP",
              "BOLETO",

              "AMIL",
              "PORTO SEGURO",
              "HAPVIDA",
              "SAÍDA DE CAIXA",
            ]
              .filter((cond) => Math.abs(totaisPorCondicao[cond] || 0) > 0)
              .map((condicao, index) => {
                const valor = totaisPorCondicao[condicao] ?? 0;
                const isSaida = condicao === "SAÍDA DE CAIXA";
                const isConvenio = ["AMIL", "PORTO SEGURO", "HAPVIDA"].includes(
                  condicao,
                );

                const iconeConvenio = (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-blue-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4"
                    />
                  </svg>
                );

                return (
                  <div
                    key={index}
                    className={`rounded-lg shadow-sm p-3 text-sm font-medium border flex items-center justify-between
                      ${
                        isSaida
                          ? "border-red-400 text-red-700 bg-red-50"
                          : isConvenio
                            ? "border-blue-200 text-blue-700 bg-blue-50"
                            : "border-green-400 text-green-700 bg-green-50"
                      }`}
                  >
                    <div>
                      <div className="uppercase text-xs tracking-widest">
                        {condicao}
                      </div>
                      <div className="text-base font-bold">
                        {isSaida ? "- " : ""}
                        R$ {Math.abs(valor).toFixed(2).replace(".", ",")}
                      </div>
                    </div>
                    <div>
                      {isConvenio ? iconeConvenio : iconesPorCondicao[condicao]}
                    </div>
                  </div>
                );
              })}
          </div>

          {/* TOTAL GERAL */}
          <div className="mt-4 bg-blue-700 text-white rounded-xl px-4 py-4 shadow flex flex-col sm:flex-row flex-wrap justify-between gap-4 font-medium">
            <div className="flex items-center gap-2">
              <Coins className="w-5 h-5 text-white" />
              <span>Entradas:</span>
              <span className="font-bold">
                R${" "}
                {Object.entries(totaisPorCondicao)
                  .filter(
                    ([cond]) =>
                      ![
                        "SAÍDA DE CAIXA",
                        "AMIL",
                        "PORTO SEGURO",
                        "HAPVIDA",
                      ].includes(cond),
                  )
                  .reduce((acc, [_, val]) => acc + val, 0)
                  .toFixed(2)
                  .replace(".", ",")}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-white font-bold">🏥 Convênios:</span>
              <span className="font-bold">
                R${" "}
                {["AMIL", "PORTO SEGURO", "HAPVIDA"]
                  .reduce((acc, key) => acc + (totaisPorCondicao[key] || 0), 0)
                  .toFixed(2)
                  .replace(".", ",")}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-white font-semibold">🧮 Total do Dia:</span>
              <span className="text-xl font-black">
                R$ {totalGeral.toFixed(2).replace(".", ",")}
              </span>
            </div>
          </div>
        </div>

        {/* TABELA / CARDS DE LANÇAMENTOS */}
        <div className="mt-8">
          <h2 className="text-xl font-bold text-blue-800 mb-4">
            Lançamentos do Dia
          </h2>

          {/* MOBILE: Cards, DESKTOP: Tabela */}
          <div className="block sm:hidden">
            {pagamentos.length === 0 ? (
              <div className="px-4 py-4 text-center text-gray-500 bg-blue-50 rounded-xl">
                Nenhum lançamento para esta data.
              </div>
            ) : (
              <div className="flex flex-col gap-4">
                {pagamentos.map((pg) => (
                  <div
                    key={pg.id}
                    className="bg-white border border-blue-200 rounded-xl shadow p-4 flex flex-col gap-2 text-blue-900"
                  >
                    <div className="flex justify-between items-center">
                      <div className="font-bold text-lg">
                        {pg.nome_paciente}
                      </div>
                      <div>
                        {pg.condicao_pagamento === "SAÍDA DE CAIXA" ? (
                          <span className="text-red-600 font-bold">
                            - R${" "}
                            {Math.abs(pg.valor).toFixed(2).replace(".", ",")}
                          </span>
                        ) : (
                          <span>
                            R${" "}
                            {parseFloat(pg.valor).toFixed(2).replace(".", ",")}
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center text-xs">
                      <span className="flex items-center gap-1">
                        <span className="font-semibold">Dentista:</span>
                        {pg.nome_dentista ||
                          dentistas.find((d) => d.id === pg.dentista_id)
                            ?.profissional ||
                          "-"}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="font-semibold">Condição:</span>
                        {pg.condicao_pagamento}
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="font-semibold">Data:</span>
                        {String(pg.data_pagamento)
                          .split("T")[0]
                          .split("-")
                          .reverse()
                          .join("/")}
                      </span>
                    </div>

                    <div className="flex gap-3 mt-2">
                      <button
                        onClick={() => handleEditar(pg)}
                        className="text-blue-600 hover:text-blue-800 transition"
                        title="Editar"
                      >
                        <Pencil className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleExcluir(pg.id)}
                        className="text-red-600 hover:text-red-800 transition"
                        title="Excluir"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* DESKTOP: Tabela */}
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full bg-white text-blue-900 rounded-xl shadow-md">
              <thead className="bg-blue-100">
                <tr>
                  <th className="px-4 py-3 text-left">Nome</th>
                  <th className="px-4 py-3 text-left">Valor (R$)</th>
                  <th className="px-4 py-3 text-left">Dentista</th>
                  <th className="px-4 py-3 text-left">Condição</th>
                  <th className="px-4 py-3 text-left">Data</th>
                  <th className="px-4 py-3 text-left">Ações</th>
                </tr>
              </thead>
              <tbody>
                {pagamentos.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-4 py-4 text-center text-gray-500"
                    >
                      Nenhum lançamento para esta data.
                    </td>
                  </tr>
                ) : (
                  pagamentos.map((pg) => (
                    <tr
                      key={pg.id}
                      className="border-b border-blue-200 hover:bg-blue-50 transition"
                    >
                      <td className="px-4 py-2 text-left">
                        {pg.nome_paciente}
                      </td>
                      <td className="px-4 py-2">
                        {pg.condicao_pagamento === "SAÍDA DE CAIXA" ? (
                          <span className="text-red-600 font-bold">
                            - R${" "}
                            {Math.abs(pg.valor).toFixed(2).replace(".", ",")}
                          </span>
                        ) : (
                          <span>
                            R${" "}
                            {parseFloat(pg.valor).toFixed(2).replace(".", ",")}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-left">
                        {pg.nome_dentista ||
                          dentistas.find((d) => d.id === pg.dentista_id)
                            ?.profissional ||
                          "-"}
                      </td>
                      <td className="px-4 py-2 text-left">
                        {pg.condicao_pagamento}
                      </td>
                      <td className="px-4 py-2">
                        {String(pg.data_pagamento)
                          .split("T")[0]
                          .split("-")
                          .reverse()
                          .join("/")}
                      </td>
                      <td className="px-4 py-2 flex gap-2">
                        <button
                          onClick={() => handleEditar(pg)}
                          className="text-blue-600 hover:text-blue-800 transition"
                          title="Editar"
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleExcluir(pg.id)}
                          className="text-red-600 hover:text-red-800 transition"
                          title="Excluir"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
